/* eslint-disable camelcase */
import { Box, Grid, TextField, Typography, makeStyles } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import colors from '../../../../assets/colors'
import filterStylesComponents from '../../../../assets/filterComponentsStyles'
import { isCpfCnpjValid } from '../../../../util/cpf_cnpj_validator'
import { cpfMask, phoneMask } from '../../../../util/utils'
import { updateBillingProfile } from '../../SubscriptionActions'

const NAME_MAX_LENGTH = 64

const useStyles = makeStyles({
  label: {
    fontSize: '12px',
    color: colors.textGray,
  },
  value: {
    fontSize: '14px',
    color: colors.primary,
    fontWeight: 'bold',
    wordBreak: 'break-word',
  },
  hint: {
    fontSize: '12px',
    color: colors.textGray,
  },
  btnSubmit: {
    width: 'auto',
    minWidth: '101px',
    whiteSpace: 'nowrap',
  },
  btnCancel: {
    height: '30px',
    borderRadius: '16px',
    backgroundColor: 'transparent',
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
    padding: '0 16px',
    margin: 0,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all .4s ease-in-out',
    '&:hover': {
      opacity: 0.7,
    },
  },
})

const formatZipCode = zip => {
  const digits = (zip || '').replace(/\D/g, '')
  return digits.length === 8 ? digits.replace(/(\d{5})(\d{3})/, '$1-$2') : zip
}

const formatPhone = phones => {
  const phone = phones?.mobile_phone || phones?.home_phone
  if (!phone?.number) return null
  return phoneMask(`${phone.area_code || ''}${phone.number}`)
}

const formatAddress = address => {
  if (!address) return null
  const cityState = [address.city, address.state].filter(Boolean).join(' - ')
  const zip = address.zip_code ? `CEP ${formatZipCode(address.zip_code)}` : ''
  return [address.line_1, cityState, zip].filter(Boolean).join(', ') || null
}

// Mensagem de erro do CPF/CNPJ, ou null quando válido. Com `partial`, um
// documento ainda incompleto não é tratado como erro (validação durante a
// digitação); só vira erro no blur ou no envio.
const validateDocument = (value, { partial = false } = {}) => {
  const digits = (value || '').replace(/\D/g, '')

  if (!digits) return partial ? null : 'Informe o CPF ou CNPJ'
  if (digits.length !== 11 && digits.length !== 14) {
    return partial ? null : 'CPF deve ter 11 dígitos e CNPJ, 14'
  }
  if (!isCpfCnpjValid(digits)) {
    return digits.length === 11 ? 'CPF inválido' : 'CNPJ inválido'
  }
  return null
}

const Field = ({ label, value }) => {
  const classes = useStyles()

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value}>{value || '-'}</Typography>
    </Box>
  )
}

// Cadastro de cobrança (customer) da lavanderia na Pagar.me, que é a fonte da
// verdade desses dados. Só nome e CPF/CNPJ são editáveis por aqui; o tipo de
// pessoa é derivado do documento pelo backend.
const BillingProfile = () => {
  const profile = useSelector(state => state.subscription.billingProfile)
  const notice = useSelector(state => state.subscription.billingProfileNotice)
  const loaded = useSelector(state => state.subscription.billingProfileLoaded)
  const dispatch = useDispatch()
  const classes = useStyles()
  const filterStyles = filterStylesComponents()

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', document: '' })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!editing) {
      setForm({
        name: profile?.name || '',
        document: cpfMask(profile?.document || ''),
      })
      setErrors({})
    }
  }, [profile, editing])

  if (!loaded) return null

  if (!profile) {
    return (
      <Grid item xs={12}>
        <Typography className={classes.hint}>{notice}</Typography>
      </Grid>
    )
  }

  const handleChange = e => {
    const { name, value } = e.target
    setForm(prev => ({
      ...prev,
      [name]: name === 'document' ? cpfMask(value) : value,
    }))
    setErrors(prev => ({
      ...prev,
      [name]:
        name === 'document' ? validateDocument(value, { partial: true }) : null,
    }))
  }

  const handleDocumentBlur = () => {
    setErrors(prev => ({ ...prev, document: validateDocument(form.document) }))
  }

  const validate = () => {
    const nextErrors = {}
    if (!form.name.trim()) nextErrors.name = 'Informe o nome'
    const documentError = validateDocument(form.document)
    if (documentError) nextErrors.document = documentError
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSaving(true)
    const ok = await dispatch(
      updateBillingProfile({
        name: form.name.trim(),
        document: form.document.replace(/\D/g, ''),
      })
    )
    setSaving(false)

    if (ok) setEditing(false)
  }

  const documentLabel = profile.document_type || 'CPF/CNPJ'

  return (
    <>
      {editing ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome"
              name="name"
              value={form.name}
              onChange={handleChange}
              inputProps={{ maxLength: NAME_MAX_LENGTH }}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="CPF/CNPJ"
              name="document"
              value={form.document}
              onChange={handleChange}
              onBlur={handleDocumentBlur}
              inputProps={{ maxLength: 18 }}
              error={!!errors.document}
              helperText={errors.document}
              fullWidth
              required
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} sm={6}>
            <Field label="Nome" value={profile.name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field
              label={documentLabel}
              value={cpfMask(profile.document || '')}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={6}>
        <Field label="E-mail" value={profile.email} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field label="Telefone" value={formatPhone(profile.phones)} />
      </Grid>
      <Grid item xs={12}>
        <Field label="Endereço" value={formatAddress(profile.address)} />
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.hint}>
          Apenas nome e CPF/CNPJ podem ser alterados por aqui.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}
        >
          {editing ? (
            <>
              <button
                type="button"
                className={classes.btnCancel}
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`${filterStyles.btnSalvar} ${classes.btnSubmit}`}
                onClick={handleSubmit}
                disabled={saving || !!errors.document}
              >
                Salvar
              </button>
            </>
          ) : (
            <button
              type="button"
              className={`${filterStyles.btnSalvar} ${classes.btnSubmit}`}
              onClick={() => setEditing(true)}
            >
              Editar
            </button>
          )}
        </Box>
      </Grid>
    </>
  )
}

export default BillingProfile
